import { fetchJSON } from '@bonnet/core/fetch';
import { cognitoAuthenticatedGet as get, cognitoAuthenticatedPost as post, cognitoAuthenticatedDelete as del, userApiCognitoAuthenticatedGet, userApiCognitoAuthenticatedDelete, userApiCognitoAuthenticatedPut, psxNotificationsCognitoAuthenticatedGet } from './fetchUtils';
export const bookmarkKeyMap = {
  bookmarkId: 'id',
  bookmarkSaved: 'isSaved',
  bookmarkAlreadySaved: 'isExisting',
  bookmarkTitle: 'title',
  bookmarkNotes: 'notes',
  bookmarkCode: 'code',
  bookMarkDeleted: 'isDeleted'
};
export const getBookmarkKeys = () => Object.keys(bookmarkKeyMap);
const standardizeMessageResponse = messages => messages.reduce((acc, msg) => {
  let severity;
  switch (msg.severity) {
    case 'WARN':
      severity = 'warning';
      break;
    case 'ERROR':
      severity = 'error';
      break;
    default:
  }
  if (!acc[severity]) {
    acc[severity] = msg.value;
  } else {
    acc[severity] = `${acc[severity]}\n${msg.value}`;
  }
  return acc;
}, {});
const standarizeBookmarkResponse = (bookmark, alerts) => ({
  bookmarkId: bookmark.id,
  bookmarkNotes: bookmark.notes,
  bookmarkTitle: bookmark.title,
  enableEmailAlerts: alerts.alertByEmail,
  enableTextAlerts: alerts.alertByText,
  overwriteFlag: alerts.overwrite
});

/**
 * Ensure only numerics exist in phone number
 * @param  {String} phone Phone Number string value
 * @return {String}       Phone Number string value
 */
export const cleanPhoneNumber = phone => phone.replace(/\D/g, '');

/**
 * Translate remote response data into expected PersonalizationClient bookmark response
 * @param  {Object}
 * @return {Object}
 */
export const createBookmark = data => Object.keys(data).reduce((acc, key) => {
  // cache data value
  const value = data[key];
  if (getBookmarkKeys().includes(key)) {
    // Create bookmark object if it doesn't already exist
    if (!acc.bookmark) {
      acc.bookmark = {};
    }

    // Set value of key in bookmark object
    acc.bookmark[bookmarkKeyMap[key]] = value;
  } else {
    acc[key] = value;
  }
  return acc;
}, {});

/**
 * @param search
 * @param email
 * @returns {Object}
 */
const registerUser = async (data, apiPath) => {
  const {
    search,
    email
  } = data;
  const query = {
    ...search,
    emailAddress: email
  };
  // Add these fake token headers to ensure that Akamai directs the request to the BFF.
  // This can be removed once the Akamai rules are cleaned up.
  const options = {
    headers: {
      accesstoken: 'any value',
      idtoken: 'any value'
    }
  };
  return post(apiPath, query, options);
};

/**
 * Handles storing and caching personalization remotely when authentication
 * @module RemoteCache
 */
export default class RemoteCache {
  constructor(config) {
    if (config === void 0) {
      config = {};
    }
    this.rootPath = config.remoteApiPath;
    this.myAtcPath = config.myAtcApiPath;
    this.accountApiPath = config.accountApiPath;
    this.userAccountApiPath = config.userAccountApiPath;
  }

  /**
   * Get fully qualified path to remote api
   * @alias RemoteCache
   * @param  {String} method name
   * @return {String} path
   */
  getRemoteApiPath(method) {
    return this.rootPath + method;
  }

  /**
   * Get path to remote light api
   * @alias RemoteCache
   * @return {String} path
   */
  getMyAtcApiPath(method) {
    return this.myAtcPath + method;
  }

  /**
   * Get fully qualified path to remote api
   * @alias RemoteCache
   * @param  {String} method name
   * @return {String} path
   */
  getAccountApiPath(method) {
    return this.accountApiPath + method;
  }

  /**
   * Get fully qualified path to remote api
   * @alias RemoteCache
   * @param  {String} method name
   * @return {String} path
   */
  getUserAccountApiPath(method) {
    return this.userAccountApiPath + method;
  }

  /**
   * @alias RemoteCache
   * @param  {Object} inventory object
   * @return {cacheEvent}
   */
  async addItem(item) {
    const remoteResponse = await post(this.getAccountApiPath('vehicle'), {
      listingId: item.id
    });
    const messages = remoteResponse.msgs && standardizeMessageResponse(remoteResponse.msgs);
    delete remoteResponse.msgs;
    const response = {
      action: 'add',
      cacheType: 'remote',
      ...createBookmark(remoteResponse),
      phone: remoteResponse.mobilePhone,
      messages
    };
    return response;
  }

  /**
   * @alias RemoteCache
   * @param  {Object} User object
   * @return {Object}
   */
  async cancelAllAlerts(id) {
    const data = {
      userId: id
    };
    return post(this.getMyAtcApiPath('savedcontent/optout'), data);
  }

  /**
   * @alias RemoteCache
   * @param  {Object} User object
   * @return {Object}
   */
  async cancelSearchAlerts(id) {
    const data = {
      userId: id
    };
    return post(this.getAccountApiPath('savedcontent/optoutsearch'), data);
  }

  /**
   * @alias RemoteCache
   * @param bookmarkId
   * @param enabledFlag
   * @param phone
   * @param targets
   * @return {cacheEvent}
   */
  async updateAlerts(_ref) {
    let {
      bookmarkId,
      enabledFlag,
      phone,
      targets
    } = _ref;
    const enableEmailAlerts = targets?.[0] === 'EMAIL' ? enabledFlag : undefined;
    const enableTextAlerts = targets?.[0] === 'SMS' ? enabledFlag : undefined;
    return post(this.getAccountApiPath('vehicle'), {
      bookmarkId,
      mobilePhone: phone,
      enableEmailAlerts,
      enableTextAlerts
    });
  }

  /**
   * @alias RemoteCache
   * @param bookmarkId
   * @param enabledFlag
   * @param phone
   * @param brand
   * @param  {Array} targets
   * @return {cacheEvent}
   */
  async updateSearchAlerts(_ref2) {
    let {
      bookmarkId,
      enabledFlag,
      phone,
      targets,
      searchName,
      brand
    } = _ref2;
    const enableEmailAlerts = targets?.[0] === 'EMAIL' ? enabledFlag : undefined;
    const enableTextAlerts = targets?.[0] === 'SMS' ? enabledFlag : undefined;
    return post(this.getAccountApiPath('search'), {
      bookmarkId,
      mobilePhone: phone,
      enableEmailAlerts,
      enableTextAlerts,
      searchName,
      brand
    });
  }

  /**
   * @alias RemoteCache
   * @param  {Object} inventory object
   * @return {cacheEvent}
   */
  async removeItem(item) {
    const response = await post(this.getAccountApiPath('deletebookmark'), item);
    const messages = response.msgs && standardizeMessageResponse(response.msgs);
    delete response.msgs;
    return {
      action: 'remove',
      cacheType: 'remote',
      listing: item,
      ...createBookmark(response),
      messages
    };
  }

  /**
   * @alias RemoteCache
   * @param  {BookmarkItem}
   * @return {cacheEvent}
   */
  async updateItem(data) {
    const {
      listing,
      bookmark
    } = data;
    let phone;

    // ensure we are sending only numerics in a string to service
    if (data.phone) {
      phone = cleanPhoneNumber(data.phone);
    }
    const response = await post(this.getAccountApiPath('vehicle'), {
      listingId: listing.id,
      mobilePhone: phone,
      ...standarizeBookmarkResponse(bookmark, data)
    });
    const messages = response.msgs && standardizeMessageResponse(response.msgs);
    return {
      action: 'update',
      cacheType: 'remote',
      bookmark: {
        ...bookmark,
        isSaved: response.bookmarkSaved,
        isExisting: response.bookmarkAlreadySaved,
        version: response.version
      },
      mobilePhone: phone,
      messages
    };
  }

  /**
   * Returns hydrated inventory objects of supplied ids
   * @alias RemoteCache
   * @param  {Array}
   * @return {Array} List of inventory objects
   */
  async hydrateItems(ids, options) {
    if (ids === void 0) {
      ids = [];
    }
    if (options === void 0) {
      options = {
        hideFilters: true
      };
    }
    const listingIds = ids.join(',');
    const listingCount = ids.length;
    const filterOptions = `${Object.entries(options).map(_ref3 => {
      let [key, value] = _ref3;
      return `&${key}=${value}`;
    })}`.replace(',', '');

    // numRecord = 0 will return error on response
    let lscLink = `/rest/lsc/listing?listingId=${listingIds}${filterOptions}`;
    if (listingCount > 0) {
      lscLink += `&numRecords=${listingCount}`;
    }
    const remoteResponse = await fetchJSON(lscLink);
    const {
      listings
    } = remoteResponse;
    // TODO: Need to translate the object into a standard AXL inventory object. This work maybe be handled in the service soon.
    return listings.map(item => ({
      id: item.id,
      type: item.listingType,
      make: item.make,
      model: item.model,
      trim: item.trim,
      title: item.title,
      year: item.year,
      pricingDetail: item.pricingDetail,
      owner: item.ownerId,
      images: item.images
    }));
  }

  /**
   * Cache multiple items remotely
   * @alias RemoteCache
   * @param  {Array}
   * @return {Object}
   */
  async addItems(ids) {
    if (ids === void 0) {
      ids = [];
    }
    const remoteResponse = await post(this.getAccountApiPath('persistQuickSaveVehicles'), {
      listingIds: ids.join(',')
    });
    const response = {
      success: false
    };
    if (Object.keys(remoteResponse).length) {
      const {
        failedToCreateBookmarkBeans = [],
        failedToDeleteBookmarkBeans = [],
        createdBookmarkBeans = [],
        deletedBookmarkBeans = []
      } = remoteResponse;
      if (failedToCreateBookmarkBeans.length || failedToDeleteBookmarkBeans.length) {
        response.success = false;
      } else {
        response.success = true;
      }
      response.createdSaves = createdBookmarkBeans.map(createBookmark);
      response.deletedSaves = deletedBookmarkBeans.map(createBookmark);
    }
    return response;
  }

  /**
   * @alias RemoteCache
   * @param  {Object} Search object
   * @return {cacheEvent}
   */
  async addSearchItem(data) {
    if (data === void 0) {
      data = {};
    }
    const actionResponse = await post(this.getAccountApiPath('search'), data);
    if (!actionResponse.bookmarkAlreadySaved && !actionResponse.bookmarkSaved) {
      actionResponse.msgs = [{
        severity: 'ERROR',
        value: 'There has been an error saving your search. Please try again later'
      }];
    }
    const messages = actionResponse.msgs && standardizeMessageResponse(actionResponse.msgs);
    delete actionResponse.msgs;
    const response = {
      action: 'add',
      cacheType: 'remote',
      ...createBookmark(actionResponse),
      phone: actionResponse.mobilePhone,
      messages
    };
    return response;
  }

  /**
   * @alias RemoteCache
   * @param  {BookmarkItem}
   * @return {cacheEvent}
   */
  async updateSearchItem(data) {
    const {
      bookmark,
      brand = ''
    } = data;
    let phone;

    // ensure we are sending only numerics in a string to service
    if (data.phone) {
      phone = data.phone.replace(/\D/g, '');
    }
    const response = await post(this.getAccountApiPath('search'), {
      ...standarizeBookmarkResponse(bookmark, data),
      mobilePhone: phone,
      brand
    });
    const messages = response.msgs && standardizeMessageResponse(response.msgs);
    return {
      action: 'update',
      cacheType: 'remote',
      bookmark: {
        ...bookmark,
        isSaved: response.bookmarkSaved,
        isExisting: response.bookmarkAlreadySaved,
        version: response.version
      },
      phone,
      messages
    };
  }

  /**
   * @alias RemoteCache
   * @param id
   * @returns {Object}
   */
  async unsaveSearchItem(_ref4) {
    let {
      id,
      version
    } = _ref4;
    const data = {
      bookmarkId: id,
      version
    };
    return post(this.getAccountApiPath('search/delete'), data);
  }

  /**
   * @alias RemoteCache
   * @param data
   * @returns {cacheEvent}
   */
  async validateProfileForm(_ref5) {
    let {
      currentPhoneNumber,
      currentZipCode,
      updatedPhoneNumber,
      updatedZipCode
    } = _ref5;
    const response = {
      success: true,
      formattedUserPhone: currentPhoneNumber
    };
    if (updatedPhoneNumber !== currentPhoneNumber || updatedZipCode && currentZipCode !== updatedZipCode) {
      const {
        success,
        zipCodeError,
        phoneNumberError,
        formattedUserPhone
      } = await post(this.getAccountApiPath('validateProfileForm'), {
        currentPhoneNumber,
        updatedPhoneNumber,
        zipCode: updatedZipCode
      });
      response.success = success;
      response.formattedUserPhone = formattedUserPhone;
      if (!success) {
        const errors = {};
        if (zipCodeError) {
          errors['profile.zip'] = zipCodeError;
        }
        if (phoneNumberError) {
          errors['profile.phones.number'] = phoneNumberError;
        }
        response.errors = errors;
      }
    }
    return response;
  }

  /**
   * @alias RemoteCache
   * @param data
   * @returns {Object}
   */
  async updateLightUserRegistration(data) {
    return registerUser(data, this.getAccountApiPath('lightuser/search'));
  }

  /**
   * @alias RemoteCache
   * @param data
   * @returns {Object}
   */
  async updateBestDealsRegistration(data) {
    return registerUser(data, this.getMyAtcApiPath('savebestdeals'));
  }

  /**
   * returns get user info
   * @alias RemoteCache
   * @return {Object}
   */
  async getUserInfo(data) {
    return get(this.getAccountApiPath('userinfo'), data);
  }

  /**
   * returns get bookmark info
   * @alias RemoteCache
   * @return {Object}
   */
  async getBookmarks() {
    return get(this.getRemoteApiPath('bookmarkInfo'));
  }

  /**
   * returns get owned vehicle
   * @alias RemoteCache
   * @return {Array}
   */
  async getOwnedVehicles() {
    return get(this.getAccountApiPath('consumer/consumer-vehicle-owners'));
  }

  /**
   * @alias RemoteCache
   * @param  {Object} vehicle object
   * @returns {Object}
   */
  async removeOwnedVehicle(itemId) {
    return del(this.getAccountApiPath('consumer/consumer-vehicle-owners'), {
      vehicleId: itemId
    });
  }

  /**
   * updates vehicle in user-api proxy for authenticated user
   * @alias RemoteCache
   * @param {Object} vehicle user-account-api shape vehicle
   * @return {}
   */
  async updateUserOwnedVehicle(vehicle) {
    return userApiCognitoAuthenticatedPut(this.getUserAccountApiPath(`vehicle/id/${vehicle.vehicleid}`), vehicle);
  }

  /**
   * retrives owned vehicles from user-api proxy for authenticated user
   * @alias RemoteCache
   * @return {}
   */
  async getUserOwnedVehicles() {
    return userApiCognitoAuthenticatedGet(this.getUserAccountApiPath('vehicles'));
  }

  /**
   * @alias RemoteCache
   * @param  {String} vehicle user-account-api vehicle ID
   * @returns {}
   */
  async removeUserOwnedVehicle(vehicleId) {
    return userApiCognitoAuthenticatedDelete(this.getUserAccountApiPath(`vehicle/id/${vehicleId}`));
  }

  /**
   * @alias RemoteCache
   * Fetches the count of unread PSX notifications
   * @returns {Number}
   */
  async getPsxNotificationsCount() {
    try {
      const rawResponse = await psxNotificationsCognitoAuthenticatedGet();
      const {
        totalUnreadNotificationsCount
      } = rawResponse || {};
      return totalUnreadNotificationsCount ?? 0;
    } catch (error) {
      console.error('Error fetching psx notifications:', error);
      return 0;
    }
  }
}